// @erp/module-cms/fe/routes/student — the MEMBER portal's CMS subtree: the
// reader (ADR-010 §2.12). ONE BARREL PER SHELL (./index.ts is the admin
// console's): a shared barrel would drag the console's factories into the
// student portal's tsc program, where their paths are not legal, and fuse the
// two screen graphs against Law F3. Parent-generic, so the shell decides where
// the subtree hangs and the module decides what is in it (ADR §4.1).
//
//   /read/$slug?locale=  — INSIDE the OPAC chrome (the person may be signed
//                          in, and the shell's account menu is theirs); the
//                          screen maps the server's four answers to cards.
//
// No loader: the read is SESSION-AWARE and its refusals are the screen's own
// states, so warming it in a loader would only start the same request a beat
// earlier — and the screen's `queryState` paints bones until it lands either
// way. `nav.ts` is untouched: readers arrive from the website, not a menu.
import { createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { Locale } from "../../schemas/index.js";
import type { AnyRoute } from "@tanstack/solid-router";

/** The reader's URL contract — `?locale=` names the row's locale; absent reads the install default. */
export interface ReaderSearch {
  locale?: string;
}

// The SAME canonicaliser the API applies (schemas/locale.ts): `?locale=HI`
// heals to `hi`, garbage heals to absent — never a router error for a link
// the website minted.
const localeParam = Locale.optional().catch(undefined);

/**
 * Self-healing: a bad or missing `?locale=` reads as "the default locale". The
 * key is RETURNED EXPLICITLY as `undefined` when healed: TanStack merges a
 * match's search over its parents', and the root passes the raw URL through,
 * so an omitted key would leave the raw garbage standing on the match.
 */
export function validateReaderSearch(input: Record<string, unknown>): ReaderSearch {
  return { locale: localeParam.parse(input.locale) };
}

/**
 * Build the reader subtree under the portal's root route. Returns the routes to
 * spread into `rootRoute.addChildren([...])` (router.tsx) beside the OPAC trees.
 */
export function makeReaderRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const readRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/read/$slug",
    validateSearch: validateReaderSearch,
  }).lazy(lazyRoute(() => import("../screens/student/Reader.js")));

  return [readRoute];
}
